import { useMemo } from 'react';
import {
  AutoFreshnessChart,
  DropDown,
  FlexColumn,
  FormControls,
  LoadingButton,
  Radio,
} from '@elementary/common-ui/components';
import {
  AutomatedTestAnomalyConfig,
  AutomatedTestConfigurationApi,
  FreshnessAnomalyDetectionChart,
  SensitivityEstimation,
  Period,
} from '@elementary/common-ui/types';
import { formatTimeValue } from '@elementary/common-ui/utils/datetime';
import { FormControlLabel, RadioGroup, Typography } from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import TimeWithUnitField from 'shared/components/TimeWithUnitField';
import TrainingPeriodSection from './TrainingPeriodSection';

const thresholdUnitOptions = [
  { value: 'hour', label: 'hours' },
  { value: 'day', label: 'days' },
  { value: 'week', label: 'weeks' },
  { value: 'month', label: 'months' },
];

type Props = {
  chartData: FreshnessAnomalyDetectionChart;
  methods: UseFormReturn<AutomatedTestConfigurationApi>;
  onSimulate: (config: AutomatedTestAnomalyConfig) => void;
  estimatedSensitivityRange: SensitivityEstimation[];
  isLoading: boolean;
  readOnly?: boolean;
  savedTrainingPeriod?: Period | null;
};

const CloudFreshnessFields = ({
  chartData,
  methods,
  onSimulate,
  estimatedSensitivityRange,
  isLoading,
  readOnly,
  savedTrainingPeriod,
}: Props) => {
  const sensitivityOptions = useMemo(
    () =>
      estimatedSensitivityRange?.map((e) => {
        const value = e.sensitivity;
        const capitalized = value.charAt(0).toUpperCase() + value.slice(1);
        const estimationLabel = e.max
          ? `${capitalized} (Up to ${formatTimeValue(e.max, 2)})`
          : `${capitalized} (Estimating...)`;
        return { value, label: estimationLabel };
      }) || [],
    [estimatedSensitivityRange],
  );

  return (
    <>
      <FormControls.FieldsWrapper>
        <FlexColumn sx={{ gap: 3 }}>
          <FlexColumn sx={{ gap: 1 }}>
            <Typography sx={{ fontWeight: 500 }}>Detection mode</Typography>
            <Typography>Please select one of the options</Typography>
          </FlexColumn>

          <RadioGroup
            sx={{ gap: 3 }}
            value={
              methods.watch('anomaly_config.fixed_threshold') == null
                ? 'auto'
                : 'manual'
            }
          >
            <FormControls.Row>
              <FormControlLabel
                sx={{ mr: 0 }}
                value="auto"
                control={<Radio size="small" />}
                disabled={readOnly}
                onChange={() =>
                  methods.setValue('anomaly_config.fixed_threshold', null)
                }
                label={
                  <>
                    <Typography component="span" sx={{ mr: 1 }}>
                      Anomaly detection - automatically detect the SLA with a
                      sensitivity level of
                    </Typography>
                    <Controller
                      control={methods.control}
                      name="anomaly_config.sensitivity"
                      disabled={
                        methods.watch('anomaly_config.fixed_threshold') != null
                      }
                      render={({ field }) => (
                        <DropDown
                          sortOptions={false}
                          options={sensitivityOptions}
                          {...field}
                          disabled={readOnly || field.disabled}
                        />
                      )}
                    />
                  </>
                }
              />
            </FormControls.Row>

            <FormControls.Row>
              <FormControlLabel
                sx={{ mr: 0 }}
                value="manual"
                control={<Radio size="small" />}
                disabled={readOnly}
                onChange={() =>
                  methods.setValue('anomaly_config.fixed_threshold', {
                    count: 1,
                    period: 'day',
                  })
                }
                label={
                  <>
                    <Typography component="span" sx={{ mr: 1 }}>
                      {`Static SLA - alert when table hasn't updated for`}
                    </Typography>
                    <Controller
                      control={methods.control}
                      name="anomaly_config.fixed_threshold"
                      render={({ field }) => (
                        <TimeWithUnitField
                          disabled={
                            readOnly ||
                            methods.watch('anomaly_config.fixed_threshold') ==
                              null
                          }
                          onChange={field.onChange}
                          count={methods.watch(
                            'anomaly_config.fixed_threshold.count',
                          )}
                          period={methods.watch(
                            'anomaly_config.fixed_threshold.period',
                          )}
                          options={thresholdUnitOptions}
                          defaultPeriod="day"
                        />
                      )}
                    />
                  </>
                }
              />
            </FormControls.Row>
          </RadioGroup>
        </FlexColumn>
      </FormControls.FieldsWrapper>

      <TrainingPeriodSection
        methods={methods}
        readOnly={readOnly}
        savedTrainingPeriod={savedTrainingPeriod}
      />

      <FormControls.FieldsWrapper>
        {!readOnly && (
          <LoadingButton
            sx={{ width: 'fit-content' }}
            variant="outlined"
            loading={isLoading}
            onClick={() => onSimulate(methods.watch('anomaly_config'))}
          >
            Simulate configuration
          </LoadingButton>
        )}

        {chartData && (
          <AutoFreshnessChart
            metrics={chartData.metrics}
            status={chartData.status}
          />
        )}
      </FormControls.FieldsWrapper>
    </>
  );
};

export default CloudFreshnessFields;
